import React, {
  useRef, useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withPolaris } from 'Components/protons/Polaris';
import { Image } from 'Components/atoms/Image';
import { Button } from 'Components/atoms/Button';
import './_style.scss';

export const SlideshowSlider = ({
  media,
  currentIndex,
  setCurrentIndex,
  context: {
    cssPrefix,
  },
  extraClassNames,
  loadStyles,
}) => {
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const checkScroll = useCallback(() => {
    const el = sliderRef.current;
    if (el) {
      const scrollable = el.scrollWidth > el.clientWidth;
      setIsScrollable(scrollable);
      const newCanScrollLeft = el.scrollLeft > 0;
      const newCanScrollRight = scrollable && el.scrollLeft < el.scrollWidth - el.clientWidth - 2;
      setCanScrollLeft(newCanScrollLeft);
      setCanScrollRight(newCanScrollRight);
    }
  }, []);

  useEffect(() => {
    const el = sliderRef.current;
    if (el) {
      checkScroll(); // Initial check

      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(el);
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);

      return () => {
        resizeObserver.disconnect();
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
    return () => {};
  }, [checkScroll]);

  useEffect(() => {
    const el = sliderRef.current;
    if (el && media.length > 0) {
      const activeThumbnail = el.children[currentIndex];
      if (activeThumbnail) {
        activeThumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, [currentIndex, media.length, checkScroll]);

  if (!media || media.length === 0) return null;

  const scroll = (direction) => {
    const slider = sliderRef.current;
    if (!slider) return;

    const { scrollLeft, children } = slider;
    const thumbnails = Array.from(children);

    let targetScrollLeft = scrollLeft;

    if (direction === 'left') {
      // Find the index of the first thumbnail that is at or after the current scroll position
      const firstVisibleIndex = thumbnails.findIndex((thumb) => thumb.offsetLeft >= scrollLeft - 1);

      if (firstVisibleIndex > 0) {
        // If the first visible thumbnail isn't the very first thumbnail, target the one before it.
        const target = thumbnails[firstVisibleIndex - 1];
        targetScrollLeft = target.offsetLeft;
      } else {
        // If we are at the beginning, scroll to the start.
        targetScrollLeft = 0;
      }
    } else {
      // Find the first thumbnail whose start is after the current scroll position
      const target = thumbnails.find((thumb) => thumb.offsetLeft > scrollLeft + 1);
      if (target) {
        targetScrollLeft = target.offsetLeft;
      } else {
        // If no target is found, it might mean we are at the far right.
        // Scroll to the end to handle edge cases.
        targetScrollLeft = slider.scrollWidth - slider.clientWidth;
      }
    }

    slider.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
  };

  const pushTracking = (buttonType) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'slideshow.button_click',
        'slideshow.element': 'slider',
        'slideshow.buttonType': buttonType,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!isScrollable) return;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scroll('left');
      pushTracking('prev');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      scroll('right');
      pushTracking('next');
    }
  };

  const handleClick = (direction) => {
    scroll(direction);
    pushTracking(direction === 'left' ? 'prev' : 'next');
  };

  const handleImageClick = (index) => {
    setCurrentIndex(index);
    pushTracking('image');
  };

  const baseClass = `${cssPrefix}__slideshow-slider`;

  return (
    <div
      className={classNames({
        [baseClass]: loadStyles,
        ...extraClassNames,
      })}
      role="group"
      aria-label="Image thumbnails"
    >
      {isScrollable && (
      <Button
        extraClassNames={{
          [`${baseClass}-arrow`]: true,
          [`${baseClass}-arrow--left`]: true,
        }}
        onClick={() => handleClick('left')}
        srLabel="Scroll thumbnails left"
        disabled={!canScrollLeft}
        loadStyles={false}
      />
      )}
      <div
        className={`${baseClass}-thumbnails`}
        ref={sliderRef}
        role="tablist"
        aria-label="Image thumbnails scrollable list"
        tabIndex={isScrollable ? 0 : undefined}
        onKeyDown={handleKeyDown}
      >
        {media.map((item, index) => (
          <Button
            key={`${item.src}-${index}`}
            role="tab"
            loadStyles={false}
            extraClassNames={{
              [`${baseClass}-thumbnail`]: true,
              'is-active': currentIndex === index,
            }}
            onClick={() => handleImageClick(index)}
            srLabel={`View image ${index + 1}`}
            ariaSelected={currentIndex === index}
          >
            <Image
              src={item.src}
              alt={item.alt || `Slideshow Slider Image Thumbnail ${index + 1}`}
              extraClassNames={{ [`${baseClass}-thumbnail-image`]: true }}
              size="slideshow_thumbnail"
              useStaticTransform
            />
          </Button>
        ))}
      </div>
      {isScrollable && (
      <Button
        extraClassNames={{
          [`${baseClass}-arrow`]: true,
          [`${baseClass}-arrow--right`]: true,
        }}
        onClick={() => handleClick('right')}
        srLabel="Scroll thumbnails right"
        disabled={!canScrollRight}
        loadStyles={false}
      />
      )}
    </div>
  );
};

SlideshowSlider.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  currentIndex: PropTypes.number,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  loadStyles: PropTypes.bool,
  media: PropTypes.arrayOf(PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string,
  })),
  setCurrentIndex: PropTypes.func,
};

SlideshowSlider.defaultProps = {
  context: {},
  currentIndex: 0,
  extraClassNames: {},
  loadStyles: true,
  media: [],
  setCurrentIndex: () => {},
};

export default withPolaris(SlideshowSlider);
